import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, Checkbox, FormControl, FormControlLabel, IconButton, InputAdornment, MenuItem, Select, TextField,
  TablePagination, Tooltip, Typography
} from '@material-ui/core'
import { KeyboardDatePicker } from '@material-ui/pickers'
import SyncIcon from '@material-ui/icons/Sync'
import GetAppIcon from '@material-ui/icons/GetApp'
import AddIcon from '@material-ui/icons/Add'
import SearchIcon from '@material-ui/icons/Search'
import ClearIcon from '@material-ui/icons/Clear'
import OpacityIcon from '@material-ui/icons/Opacity'
import QueueList from './v4/QueueList'
import SiteDetailPanel from './v4/SiteDetailPanel'
import FieldActivityPanel from './v4/FieldActivityPanel'
import ReviewTab from './v4/ReviewTab'
import { useV4Styles, tokens } from './v4/v4Styles'

const statChips = [
  { key: 'overdue', label: 'Overdue', bgColor: '#ffebee', textColor: '#c62828' },
  { key: 'never_done', label: 'Never Done', bgColor: '#fce4ec', textColor: '#ad1457' },
  { key: 'needs_completion', label: 'Ticket Cleanup', bgColor: '#f3e5f5', textColor: '#7b1fa2' },
  { key: 'due_soon', label: 'Due Soon', bgColor: '#fff3e0', textColor: '#e65100' },
  { key: 'ok', label: 'OK', bgColor: '#e8f5e9', textColor: '#2e7d32' },
  { key: 'queue_in_progress', label: 'In Progress', bgColor: '#e3f2fd', textColor: '#1565c0' },
  { key: 'queue_partial', label: 'Partial', bgColor: '#fff8e1', textColor: '#f57f17' },
  { key: 'packet_ready', label: 'Ready Packets', bgColor: '#e8f5e9', textColor: '#2e7d32' },
  { key: 'packet_missing', label: 'Missing Info', bgColor: '#fff8e1', textColor: '#f57f17' }
]

const normalizeSiteKeyValue = (value) => String(value || '').trim().toLowerCase()

const getClientDedupeKey = (item = {}) => {
  if (item.canonicalKey) return item.canonicalKey
  if (item.dedupeKey) return item.dedupeKey
  if (item.canonicalPropertyId) return `prop:${item.canonicalPropertyId}`
  if (item.aspirePropertyId) return `prop:${item.aspirePropertyId}`
  if (item.jobsite?.aspirePropertyId) return `prop:${item.jobsite.aspirePropertyId}`
  if (item.jobsite?._id) return `job:${item.jobsite._id}`
  if (item.jobsite) return `job:${item.jobsite}`
  return `site:${normalizeSiteKeyValue(item.siteName)}|${normalizeSiteKeyValue(item.address)}`
}

const getClientScheduleIds = (item = {}) => new Set([
  item._id,
  ...(item.duplicateScheduleItemIds || [])
].map(id => String(id)).filter(Boolean))

const isSameClientScheduleItem = (left = {}, right = {}) => {
  const leftIds = getClientScheduleIds(left)
  const rightIds = getClientScheduleIds(right)
  for (const id of leftIds) {
    if (rightIds.has(id)) return true
  }
  return getClientDedupeKey(left) === getClientDedupeKey(right)
}

const mergeClientScheduleItem = (base = {}, override = {}) => {
  const duplicateScheduleItemIds = [...new Set([
    ...getClientScheduleIds(base),
    ...getClientScheduleIds(override)
  ])]

  return {
    ...base,
    ...override,
    _id: base._id || override._id,
    canonicalKey: base.canonicalKey || override.canonicalKey || getClientDedupeKey({ ...base, ...override }),
    canonicalPropertyId: base.canonicalPropertyId || override.canonicalPropertyId || base.aspirePropertyId || override.aspirePropertyId || null,
    dedupeKey: base.dedupeKey || override.dedupeKey || getClientDedupeKey({ ...base, ...override }),
    duplicateScheduleItemIds,
    pendingTickets: override.pendingTickets?.length ? override.pendingTickets : (base.pendingTickets || []),
    lastAppliedTicket: override.lastAppliedTicket || base.lastAppliedTicket || null,
    packetMissingFields: override.packetMissingFields?.length ? override.packetMissingFields : (base.packetMissingFields || []),
    siteInfo: { ...(base.siteInfo || {}), ...(override.siteInfo || {}) },
    areaSummary: override.areaSummary?.areaCount || override.areaSummary?.totalAcres ? override.areaSummary : base.areaSummary,
    fieldActivitySummary: override.fieldActivitySummary?.totalCount ? override.fieldActivitySummary : base.fieldActivitySummary
  }
}

const HerbicideQueueV4Dashboard = (props) => {
  const classes = useV4Styles()
  const history = useHistory()
  const { url } = props

  // Data
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [syncMessage, setSyncMessage] = useState('')
  const [totalCount, setTotalCount] = useState(0)
  const [stats, setStats] = useState({})
  const [selectedId, setSelectedId] = useState(null)
  const [pinnedItem, setPinnedItem] = useState(null)
  const [activeTab, setActiveTab] = useState('queue')
  const [reviewCount, setReviewCount] = useState(0)
  const [detailDirty, setDetailDirty] = useState(false)
  const detailDirtyRef = useRef(false)

  const handleDetailDirtyChange = useCallback((dirty) => {
    detailDirtyRef.current = dirty
    setDetailDirty(dirty)
  }, [])

  // Filters
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(50)
  const [statusFilter, setStatusFilter] = useState('all')
  const [queueFilter, setQueueFilter] = useState('all')
  const [packetFilter, setPacketFilter] = useState('all')
  const [territoryFilter, setTerritoryFilter] = useState('all')
  const [contractFilter, setContractFilter] = useState('contracted_priority')
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')

  // Dialogs
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false)
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false)
  const [ignoreDialogOpen, setIgnoreDialogOpen] = useState(false)
  const [dialogItem, setDialogItem] = useState(null)
  const [completeDate, setCompleteDate] = useState(null)
  const [completeNotes, setCompleteNotes] = useState('')
  const [completeScopedAreas, setCompleteScopedAreas] = useState(true)
  const [overrideDate, setOverrideDate] = useState(null)
  const [overrideReason, setOverrideReason] = useState('')
  const [ignoreReason, setIgnoreReason] = useState('')

  // One-Time Job dialog
  const [oneTimeOpen, setOneTimeOpen] = useState(false)
  const [oneTimeStep, setOneTimeStep] = useState('site')
  const [siteSearch, setSiteSearch] = useState('')
  const [siteResults, setSiteResults] = useState([])
  const [siteSearching, setSiteSearching] = useState(false)
  const [selectedSite, setSelectedSite] = useState(null)
  const [createSiteMode, setCreateSiteMode] = useState(false)
  const [newSite, setNewSite] = useState({ name: '', address: '' })
  const [selectedContact, setSelectedContact] = useState(null)
  const [createContactMode, setCreateContactMode] = useState(false)
  const [newContact, setNewContact] = useState({ firstName: '', lastName: '', companyName: '', phone: '', email: '' })
  const [siteContacts, setSiteContacts] = useState([])
  const [jobDetails, setJobDetails] = useState({
    sprayMethod: 'broadcast',
    priority: 'normal',
    blueDye: 'no',
    notes: ''
  })

  // Territories for filter dropdown
  const [territories, setTerritories] = useState([])

  const displayedItems = pinnedItem
    ? (() => {
        const matchingIndex = items.findIndex(item => isSameClientScheduleItem(item, pinnedItem))
        if (matchingIndex < 0) return [pinnedItem, ...items]
        return items.map((item, index) => (
          index === matchingIndex ? mergeClientScheduleItem(item, pinnedItem) : item
        ))
      })()
    : items
  const selectedItem = displayedItems.find(i => i._id === selectedId) || null
  const hasActiveFilters = Boolean(
    searchTerm ||
    statusFilter !== 'all' ||
    queueFilter !== 'all' ||
    packetFilter !== 'all' ||
    territoryFilter !== 'all' ||
    contractFilter !== 'contracted_priority'
  )

  const clearQueueFilters = useCallback(() => {
    setSearchTerm('')
    setDebouncedSearchTerm('')
    setStatusFilter('all')
    setQueueFilter('all')
    setPacketFilter('all')
    setTerritoryFilter('all')
    setContractFilter('contracted_priority')
    setPage(0)
  }, [])

  const fetchItems = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true)
    try {
      const params = {
        page, limit: rowsPerPage,
        sortBy: 'queuePriority', sortOrder: 'desc',
        packetStatus: packetFilter
      }
      if (statusFilter !== 'all') params.status = statusFilter
      if (queueFilter !== 'all') params.queueStatus = queueFilter
      if (territoryFilter !== 'all') params.territory = territoryFilter
      if (contractFilter === 'contracted') params.contracted = 'true'
      else if (contractFilter === 'contracted_priority') params.contracted = 'priority'
      if (debouncedSearchTerm) params.search = debouncedSearchTerm

      const resp = await axios.get(`${url}/api/herbicide-schedule`, { params })
      const nextItems = resp.data.items || []
      setItems(nextItems)
      setTotalCount(resp.data.total || 0)
      setSelectedId(prev => {
        if (!nextItems.length) return null
        // If user has unsaved edits and current item is still in the list, keep it
        if (detailDirtyRef.current && prev && nextItems.some(i => i._id === prev)) return prev
        if (!prev || !nextItems.some(i => i._id === prev)) return nextItems[0]._id
        return prev
      })
    } catch (err) {
      console.error('Error fetching queue items:', err)
    }
    if (!silent) setLoading(false)
  }, [url, page, rowsPerPage, statusFilter, queueFilter, packetFilter, territoryFilter, contractFilter, debouncedSearchTerm])

  const fetchStats = useCallback(async () => {
    try {
      const resp = await axios.get(`${url}/api/herbicide-schedule/stats`)
      setStats(resp.data || {})
    } catch (err) {
      console.error('Error fetching stats:', err)
    }
  }, [url])

  const fetchTerritories = useCallback(async () => {
    try {
      const resp = await axios.get(`${url}/api/hmi/genericGetRequest?type=HERBICIDE_TERRITORY_DETAILS`)
      setTerritories(resp.data?.data?.existingTerritories || [])
    } catch (err) {
      console.error('Error fetching territories:', err)
    }
  }, [url])

  useEffect(() => { fetchItems() }, [fetchItems])
  useEffect(() => { fetchStats() }, [fetchStats])
  useEffect(() => { fetchTerritories() }, [fetchTerritories])
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim())
    }, 300)
    return () => window.clearTimeout(timeoutId)
  }, [searchTerm])
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      fetchItems({ silent: true })
      fetchStats()
    }, 60000)
    return () => window.clearInterval(intervalId)
  }, [fetchItems, fetchStats])

  useEffect(() => {
    const fetchReviewCount = async () => {
      try {
        const resp = await axios.get(`${url}/api/herbicide-schedule/review-items`)
        setReviewCount(resp.data?.counts?.total || 0)
      } catch (err) { /* silent */ }
    }
    fetchReviewCount()
    const interval = setInterval(fetchReviewCount, 60000)
    return () => clearInterval(interval)
  }, [url])

  const handleSync = async () => {
    setSyncing(true)
    setSyncMessage('')
    try {
      await axios.post(`${url}/api/herbicide-schedule/sync`)
      await Promise.all([fetchItems(), fetchStats()])
      setSyncMessage('Sync complete')
      setTimeout(() => setSyncMessage(''), 5000)
    } catch (err) {
      setSyncMessage(err.response?.data?.error || 'Sync failed')
      setTimeout(() => setSyncMessage(''), 10000)
    }
    setSyncing(false)
  }

  const handleExport = async () => {
    try {
      const resp = await axios.get(`${url}/api/herbicide-schedule/export`, { responseType: 'blob' })
      const blobUrl = window.URL.createObjectURL(new Blob([resp.data]))
      const link = document.createElement('a')
      link.href = blobUrl
      link.setAttribute('download', `herbicide_queue_${new Date().toISOString().slice(0, 10)}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(blobUrl)
    } catch (err) {
      console.error('Error exporting:', err)
    }
  }

  const handleSave = async (itemId, draft) => {
    setSaving(true)
    try {
      await axios.put(`${url}/api/herbicide-schedule/${itemId}/queue`, draft)
      await Promise.all([fetchItems(), fetchStats()])
    } catch (err) {
      console.error('Error saving queue settings:', err)
      throw err
    } finally {
      setSaving(false)
    }
  }

  const handleQueueSelect = (itemId) => {
    if (items.some(item => item._id === itemId)) setPinnedItem(null)
    setSelectedId(itemId)
  }

  const handleActivitySelect = async (itemId, activity = null) => {
    if (!itemId) return
    const existing = displayedItems.find(item => (
      item._id === itemId ||
      (item.duplicateScheduleItemIds || []).map(String).includes(String(itemId)) ||
      (activity?.dedupeKey && getClientDedupeKey(item) === activity.dedupeKey)
    ))
    if (existing) {
      setSelectedId(existing._id)
    }

    try {
      const resp = await axios.get(`${url}/api/herbicide-schedule/${itemId}`)
      const item = resp.data?.item
      if (item?._id) {
        const dedupedExisting = displayedItems.find(existingItem => isSameClientScheduleItem(existingItem, item))
        if (dedupedExisting) {
          setPinnedItem(mergeClientScheduleItem(dedupedExisting, item))
          setSelectedId(dedupedExisting._id)
          return
        }

        setPinnedItem(item)
        setSelectedId(item._id)
        return
      }
    } catch (err) {
      console.error('Error loading activity schedule item:', err)
    }

    if (existing) {
      setSelectedId(existing._id)
    }
  }

  const handleViewInQueue = async (scheduleItemId) => {
    setActiveTab('queue')
    await handleActivitySelect(scheduleItemId)
  }

  // Dialog handlers
  const openComplete = (item) => { setDialogItem(item); setCompleteDate(new Date()); setCompleteNotes(''); setCompleteScopedAreas(true); setCompleteDialogOpen(true) }
  const openOverride = (item) => {
    setDialogItem(item)
    setOverrideDate(item.overriddenNextDate ? new Date(item.overriddenNextDate) : (item.nextScheduledDate ? new Date(item.nextScheduledDate) : new Date()))
    setOverrideReason('')
    setOverrideDialogOpen(true)
  }
  const openIgnore = (item) => { setDialogItem(item); setIgnoreReason(''); setIgnoreDialogOpen(true) }

  const submitComplete = async () => {
    if (!dialogItem || !completeDate) return
    try {
      await axios.put(`${url}/api/herbicide-schedule/${dialogItem._id}/complete`, {
        completedDate: completeDate,
        notes: completeNotes,
        allScopedAreasComplete: completeScopedAreas
      })
      setCompleteDialogOpen(false)
      await Promise.all([fetchItems(), fetchStats()])
    } catch (err) { console.error('Error marking complete:', err) }
  }

  const submitOverride = async () => {
    if (!dialogItem || !overrideDate) return
    try {
      await axios.put(`${url}/api/herbicide-schedule/${dialogItem._id}/override`, { date: overrideDate, reason: overrideReason })
      setOverrideDialogOpen(false)
      await Promise.all([fetchItems(), fetchStats()])
    } catch (err) { console.error('Error overriding date:', err) }
  }

  const submitIgnore = async () => {
    if (!dialogItem) return
    try {
      await axios.put(`${url}/api/herbicide-schedule/${dialogItem._id}/ignore`, {
        reason: dialogItem.ignored ? '' : (ignoreReason || 'Manually ignored')
      })
      setIgnoreDialogOpen(false)
      await Promise.all([fetchItems(), fetchStats()])
    } catch (err) { console.error('Error toggling ignore:', err) }
  }

  // One-Time Job handlers
  const oneTimeSteps = ['site', 'contact', 'details', 'review']

  const openOneTimeDialog = () => {
    setOneTimeStep('site')
    setSiteSearch('')
    setSiteResults([])
    setSelectedSite(null)
    setCreateSiteMode(false)
    setNewSite({ name: '', address: '' })
    setSelectedContact(null)
    setCreateContactMode(false)
    setNewContact({ firstName: '', lastName: '', companyName: '', phone: '', email: '' })
    setSiteContacts([])
    setJobDetails({ sprayMethod: 'broadcast', priority: 'normal', blueDye: 'no', notes: '' })
    setOneTimeOpen(true)
  }

  const searchSites = async (query) => {
    setSiteSearch(query)
    if (query.length < 2) { setSiteResults([]); return }
    setSiteSearching(true)
    try {
      const resp = await axios.get(`${url}/api/herbicide-schedule`, {
        params: { search: query, limit: 20, page: 0 }
      })
      setSiteResults(resp.data.items || [])
    } catch (err) { console.error('Error searching sites:', err) }
    setSiteSearching(false)
  }

  const selectSite = async (site) => {
    setSelectedSite(site)
    setCreateSiteMode(false)
    if (site.jobsite) {
      try {
        const resp = await axios.get(`${url}/api/herbicide-schedule/${site._id}/contacts`)
        setSiteContacts(resp.data || [])
      } catch (err) { console.error('Error fetching contacts:', err) }
    }
    setOneTimeStep('contact')
  }

  const selectCreateSite = () => {
    setSelectedSite(null)
    setCreateSiteMode(true)
    setOneTimeStep('contact')
  }

  const selectContact = (contact) => {
    setSelectedContact(contact)
    setCreateContactMode(false)
    setOneTimeStep('details')
  }

  const canSubmitOneTime = () => {
    const hasSite = selectedSite || (createSiteMode && newSite.name.trim())
    const hasContact = selectedContact || (createContactMode && newContact.firstName.trim() && (newContact.phone.trim() || newContact.email.trim()))
    return hasSite && hasContact
  }

  const canSubmitExisting = () => selectedSite && selectedSite._id && (selectedContact || createContactMode)

  const [submittingOneTime, setSubmittingOneTime] = useState(false)

  const submitOneTimeJob = async () => {
    if (!canSubmitOneTime()) return
    setSubmittingOneTime(true)
    try {
      if (selectedSite && selectedSite._id) {
        await axios.post(`${url}/api/herbicide-schedule/one-time`, {
          scheduleItemId: selectedSite._id,
          contactId: selectedContact?._id || null,
          sprayMethod: jobDetails.sprayMethod,
          priority: jobDetails.priority,
          blueDye: jobDetails.blueDye,
          notes: jobDetails.notes
        })
        setOneTimeOpen(false)
        await Promise.all([fetchItems(), fetchStats()])
      } else {
        console.log('TODO: create new site + contact', { newSite, newContact, jobDetails })
        setOneTimeOpen(false)
      }
    } catch (err) {
      console.error('Error creating one-time job:', err)
    }
    setSubmittingOneTime(false)
  }

  return (
    <div className={classes.root}>
      {/* Stats Bar */}
      <div className={classes.statsBar}>
        <Typography variant="subtitle2" style={{ fontWeight: 700, marginRight: 12, color: tokens.text.primary }}>Herbicide Queue</Typography>
        {statChips.map(chip => (
          <Chip
            key={chip.key}
            size="small"
            label={`${stats[chip.key] || 0} ${chip.label}`}
            style={{
              backgroundColor: statusFilter === chip.key ? chip.textColor : chip.bgColor,
              color: statusFilter === chip.key ? '#fff' : chip.textColor,
              fontWeight: 600, fontSize: 12, cursor: 'pointer',
              padding: '2px 8px'
            }}
            onClick={() => {
              if (chip.key.startsWith('packet_')) {
                setPacketFilter(packetFilter === chip.key.replace('packet_', '') ? 'all' : chip.key.replace('packet_', ''))
              } else if (chip.key.startsWith('queue_')) {
                const nextQueue = chip.key.replace('queue_', '').replace(/_/g, '-')
                setQueueFilter(queueFilter === nextQueue ? 'all' : nextQueue)
              } else {
                setStatusFilter(statusFilter === chip.key ? 'all' : chip.key)
              }
              setPage(0)
            }}
          />
        ))}
        <Typography variant="caption" style={{ color: tokens.accent.blue, marginLeft: 8 }}>{stats.contracted || 0} contracted</Typography>
        <Typography variant="caption" style={{ color: tokens.text.muted, marginLeft: 'auto' }}>{stats.total || 0} total</Typography>
        <Box ml={1.5} display="flex" style={{ gap: 6 }}>
          {syncMessage && <Typography variant="caption" style={{ color: syncMessage.includes('complete') ? '#4caf50' : '#ff9800', alignSelf: 'center' }}>{syncMessage}</Typography>}
          <Button
            size="small" variant="contained" color="primary"
            startIcon={syncing ? <CircularProgress size={14} color="inherit" /> : <SyncIcon />}
            onClick={handleSync} disabled={syncing}
            style={{ fontSize: 11 }}
          >
            {syncing ? 'Syncing...' : 'Sync'}
          </Button>
          <Button size="small" variant="outlined" startIcon={<GetAppIcon />} onClick={handleExport} style={{ fontSize: 11 }}>
            Export
          </Button>
          <Button size="small" variant="outlined" startIcon={<OpacityIcon />} onClick={() => history.push('/water-access')} style={{ fontSize: 11 }}>
            Water Access
          </Button>
          <Button
            size="small" variant="contained"
            startIcon={<AddIcon />}
            onClick={openOneTimeDialog}
            style={{ fontSize: 11, backgroundColor: tokens.accent.brown, color: '#fff' }}
          >
            One-Time Job
          </Button>
        </Box>
      </div>

      {/* Tab Bar */}
      <div style={{
        padding: '0 16px',
        background: '#fff',
        borderBottom: '2px solid #e0e0e0',
        display: 'flex',
        gap: 0
      }}>
        <div
          onClick={() => setActiveTab('queue')}
          style={{
            padding: '10px 20px',
            fontSize: 13,
            color: activeTab === 'queue' ? '#1565c0' : '#999',
            fontWeight: activeTab === 'queue' ? 700 : 400,
            cursor: 'pointer',
            borderBottom: activeTab === 'queue' ? '2px solid #1565c0' : '2px solid transparent',
            marginBottom: -2
          }}
        >
          Queue
        </div>
        <div
          onClick={() => setActiveTab('review')}
          style={{
            padding: '10px 20px',
            fontSize: 13,
            color: activeTab === 'review' ? '#1565c0' : '#999',
            fontWeight: activeTab === 'review' ? 700 : 400,
            cursor: 'pointer',
            borderBottom: activeTab === 'review' ? '2px solid #1565c0' : '2px solid transparent',
            marginBottom: -2
          }}
        >
          Review {reviewCount > 0 && (
            <span style={{
              background: '#FF9800',
              color: '#fff',
              fontSize: 10,
              padding: '1px 7px',
              borderRadius: 10,
              fontWeight: 700,
              marginLeft: 4
            }}>{reviewCount}</span>
          )}
        </div>
      </div>

      <div className={classes.contentShell}>
        {activeTab === 'queue' && (
        <>
        <div className={classes.workspacePane}>
          {/* Filter Bar */}
          <div className={classes.filterBar}>
            <TextField
              variant="outlined" size="small" placeholder="Search sites..."
              value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setPage(0) }}
              style={{ flex: '0 1 300px', minWidth: 180 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ fontSize: 18, color: tokens.text.hint }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Clear search"
                      onClick={() => {
                        setSearchTerm('')
                        setDebouncedSearchTerm('')
                        setPage(0)
                      }}
                      edge="end"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
            />
            <FormControl variant="outlined" size="small" style={{ minWidth: 110 }}>
              <Select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(0) }}
                displayEmpty
                renderValue={(val) => val === 'all' ? 'Spray Status: All' : `Spray: ${val.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}`}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="overdue">Overdue</MenuItem>
                <MenuItem value="never_done">Never Done</MenuItem>
                <MenuItem value="due_soon">Due Soon</MenuItem>
                <MenuItem value="ok">OK</MenuItem>
              </Select>
            </FormControl>
            <FormControl variant="outlined" size="small" style={{ minWidth: 100 }}>
              <Select
                value={queueFilter}
                onChange={(e) => { setQueueFilter(e.target.value); setPage(0) }}
                displayEmpty
                renderValue={(val) => val === 'all' ? 'Queue: All' : `Queue: ${val.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}`}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="ready">Ready</MenuItem>
                <MenuItem value="assigned">Assigned</MenuItem>
                <MenuItem value="in-progress">In Progress</MenuItem>
                <MenuItem value="partial">Partial</MenuItem>
                <MenuItem value="submitted">Needs Review</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </FormControl>
            <FormControl variant="outlined" size="small" style={{ minWidth: 100 }}>
              <Select
                value={packetFilter}
                onChange={(e) => { setPacketFilter(e.target.value); setPage(0) }}
                displayEmpty
                renderValue={(val) => val === 'all' ? 'Packet: All' : `Packet: ${val.replace(/\b\w/g, c => c.toUpperCase())}`}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="ready">Ready</MenuItem>
                <MenuItem value="missing">Needs Info</MenuItem>
              </Select>
            </FormControl>
            {territories.length > 0 && (
              <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
                <Select
                  value={territoryFilter}
                  onChange={(e) => { setTerritoryFilter(e.target.value); setPage(0) }}
                  displayEmpty
                  renderValue={(val) => {
                    if (val === 'all') return 'Territory: All'
                    const t = territories.find(terr => terr._id === val)
                    return `Territory: ${t?.name || val}`
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  {territories.map(t => <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>)}
                </Select>
              </FormControl>
            )}
            <FormControl variant="outlined" size="small" style={{ minWidth: 130 }}>
              <Select
                value={contractFilter}
                onChange={(e) => { setContractFilter(e.target.value); setPage(0) }}
                displayEmpty
                renderValue={(val) => val === 'contracted_priority' ? 'Contracted & Priority' : val === 'contracted' ? 'Contracted Only' : 'All Properties'}
              >
                <MenuItem value="contracted_priority">Contracted & Priority</MenuItem>
                <MenuItem value="contracted">Contracted Only</MenuItem>
                <MenuItem value="all">All Properties</MenuItem>
              </Select>
            </FormControl>
          </div>

          {/* Main Content: Queue + Detail */}
          <div className={classes.mainContent}>
            <QueueList items={displayedItems} loading={loading} selectedId={selectedId} onSelect={handleQueueSelect} />
            <SiteDetailPanel
              item={selectedItem}
              loading={loading}
              hasActiveFilters={hasActiveFilters}
              onClearFilters={clearQueueFilters}
              url={url}
              saving={saving}
              onSave={handleSave}
              onComplete={openComplete}
              onOverride={openOverride}
              onIgnore={openIgnore}
              onDirtyChange={handleDetailDirtyChange}
            />
          </div>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[25, 50, 100]}
            onChangePage={(e, p) => setPage(p)}
            onChangeRowsPerPage={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
            className={classes.footerBar}
          />
        </div>
        <FieldActivityPanel url={url} selectedId={selectedId} selectedItem={selectedItem} onSelectSite={handleActivitySelect} />
        </>
        )}

        {activeTab === 'review' && (
          <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
            <ReviewTab url={url} onViewInQueue={handleViewInQueue} />
          </div>
        )}
      </div>

      {/* Complete Dialog */}
      <Dialog open={completeDialogOpen} onClose={() => setCompleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mark Application Complete</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>Site: <strong>{dialogItem?.siteName}</strong></Typography>
          <KeyboardDatePicker label="Completion Date" format="MM/dd/yyyy" value={completeDate} onChange={setCompleteDate} fullWidth margin="normal" inputVariant="outlined" />
          <FormControlLabel
            control={
              <Checkbox
                checked={completeScopedAreas}
                onChange={(e) => setCompleteScopedAreas(e.target.checked)}
                color="primary"
              />
            }
            label="All scoped areas completed"
          />
          <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: -6, marginBottom: 8 }}>
            When checked, the current spray scope is closed out in spray progress using the latest operation mix/load context.
          </Typography>
          <TextField label="Notes (optional)" value={completeNotes} onChange={(e) => setCompleteNotes(e.target.value)} fullWidth margin="normal" variant="outlined" multiline rows={3} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={submitComplete} color="primary" variant="contained">Mark Complete</Button>
        </DialogActions>
      </Dialog>

      {/* Override Dialog */}
      <Dialog open={overrideDialogOpen} onClose={() => setOverrideDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Override Scheduled Date</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>Site: <strong>{dialogItem?.siteName}</strong></Typography>
          <KeyboardDatePicker label="New Scheduled Date" format="MM/dd/yyyy" value={overrideDate} onChange={setOverrideDate} fullWidth margin="normal" inputVariant="outlined" />
          <TextField label="Reason (optional)" value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} fullWidth margin="normal" variant="outlined" multiline rows={2} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverrideDialogOpen(false)}>Cancel</Button>
          <Button onClick={submitOverride} color="primary" variant="contained">Save Override</Button>
        </DialogActions>
      </Dialog>

      {/* Ignore Dialog */}
      <Dialog open={ignoreDialogOpen} onClose={() => setIgnoreDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{dialogItem?.ignored ? 'Unignore Site' : 'Ignore Site'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>Site: <strong>{dialogItem?.siteName}</strong></Typography>
          {!dialogItem?.ignored && (
            <TextField label="Reason (optional)" value={ignoreReason} onChange={(e) => setIgnoreReason(e.target.value)} fullWidth margin="normal" variant="outlined" multiline rows={2} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIgnoreDialogOpen(false)}>Cancel</Button>
          <Button onClick={submitIgnore} color={dialogItem?.ignored ? 'primary' : 'secondary'} variant="contained">
            {dialogItem?.ignored ? 'Unignore' : 'Ignore'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* One-Time Job Dialog */}
      <Dialog open={oneTimeOpen} onClose={() => setOneTimeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <span>New One-Time Herbicide Job</span>
            <Box display="flex" style={{ gap: 4 }}>
              {oneTimeSteps.map((step, i) => (
                <Box
                  key={step}
                  style={{
                    width: 8, height: 8, borderRadius: '50%',
                    backgroundColor: oneTimeStep === step ? tokens.accent.brown : (i < oneTimeSteps.indexOf(oneTimeStep) ? '#4caf50' : '#e0e0e0')
                  }}
                />
              ))}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent style={{ minHeight: 300 }}>
          {/* Step 1: Select or Create Site */}
          {oneTimeStep === 'site' && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>Find a jobsite or create a new one</Typography>
              <TextField
                variant="outlined" size="small" fullWidth
                placeholder="Search by name or address..."
                value={siteSearch}
                onChange={(e) => searchSites(e.target.value)}
                style={{ marginBottom: 12 }}
              />
              {siteSearching && <Box display="flex" justifyContent="center" p={2}><CircularProgress size={20} /></Box>}
              {siteResults.length > 0 && (
                <Box style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 4 }}>
                  {siteResults.map(site => (
                    <Box
                      key={site._id}
                      onClick={() => selectSite(site)}
                      style={{
                        padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f5f5f5',
                        backgroundColor: selectedSite?._id === site._id ? '#e3f2fd' : 'transparent'
                      }}
                      onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#f5f5f5' }}
                      onMouseOut={(e) => { e.currentTarget.style.backgroundColor = selectedSite?._id === site._id ? '#e3f2fd' : 'transparent' }}
                    >
                      <Typography variant="body2" style={{ fontWeight: 600, fontSize: 13 }}>{site.siteName || '(unnamed)'}</Typography>
                      <Typography variant="caption" color="textSecondary">{site.address || '\u2014'}</Typography>
                      {site.hasHerbicideContract && (
                        <Typography variant="caption" style={{ color: '#2e7d32', marginLeft: 8 }}>Contracted</Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
              {siteSearch.length >= 2 && !siteSearching && siteResults.length === 0 && (
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                  No sites found for &ldquo;{siteSearch}&rdquo;
                </Typography>
              )}
              <Box mt={2}>
                <Tooltip title="Coming soon — new site creation is not yet available">
                  <span>
                    <Button
                      variant="outlined" size="small" startIcon={<AddIcon />}
                      disabled
                      style={{ color: tokens.text.muted, borderColor: '#e0e0e0' }}
                    >
                      Create New Jobsite
                    </Button>
                  </span>
                </Tooltip>
              </Box>
              {createSiteMode && (
                <Box mt={2} p={2} style={{ border: '1px solid #e0e0e0', borderRadius: 4, backgroundColor: '#fafafa' }}>
                  <Typography variant="subtitle2" gutterBottom>New Jobsite</Typography>
                  <TextField
                    label="Site Name" variant="outlined" size="small" fullWidth
                    value={newSite.name} onChange={(e) => setNewSite({ ...newSite, name: e.target.value })}
                    style={{ marginBottom: 8 }}
                  />
                  <TextField
                    label="Address" variant="outlined" size="small" fullWidth
                    value={newSite.address} onChange={(e) => setNewSite({ ...newSite, address: e.target.value })}
                  />
                  <Box mt={1}>
                    <Button
                      size="small" variant="contained" disabled={!newSite.name.trim()}
                      onClick={() => setOneTimeStep('contact')}
                      style={{ backgroundColor: newSite.name.trim() ? tokens.accent.brown : undefined, color: '#fff' }}
                    >
                      Next: Add Contact
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* Step 2: Select or Create Contact */}
          {oneTimeStep === 'contact' && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Contact for: <strong>{selectedSite?.siteName || newSite.name || '(new site)'}</strong>
              </Typography>
              {siteContacts.length > 0 && (
                <>
                  <Typography variant="caption" color="textSecondary" style={{ marginBottom: 8, display: 'block' }}>
                    Existing contacts for this site:
                  </Typography>
                  <Box style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 4, marginBottom: 12 }}>
                    {siteContacts.map((c, i) => (
                      <Box
                        key={c._id || i}
                        onClick={() => selectContact(c)}
                        style={{
                          padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f5f5f5',
                          backgroundColor: selectedContact?._id === c._id ? '#e3f2fd' : 'transparent'
                        }}
                        onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#f5f5f5' }}
                        onMouseOut={(e) => { e.currentTarget.style.backgroundColor = selectedContact?._id === c._id ? '#e3f2fd' : 'transparent' }}
                      >
                        <Typography variant="body2" style={{ fontWeight: 600, fontSize: 13 }}>
                          {c.firstName || ''} {c.lastName || ''} {c.companyName ? `(${c.companyName})` : ''}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {[c.phoneNumber || c.phone, c.email].filter(Boolean).join(' \u00B7 ') || 'No contact info'}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
              <Button
                variant="outlined" size="small" startIcon={<AddIcon />}
                onClick={() => setCreateContactMode(true)}
                style={{ color: tokens.accent.brown, borderColor: tokens.accent.brown, marginBottom: 12 }}
              >
                Create New Contact
              </Button>
              {createContactMode && (
                <Box p={2} style={{ border: '1px solid #e0e0e0', borderRadius: 4, backgroundColor: '#fafafa' }}>
                  <Typography variant="subtitle2" gutterBottom>New Contact</Typography>
                  <Box display="flex" style={{ gap: 8, marginBottom: 8 }}>
                    <TextField
                      label="First Name" variant="outlined" size="small" style={{ flex: 1 }}
                      value={newContact.firstName} onChange={(e) => setNewContact({ ...newContact, firstName: e.target.value })}
                    />
                    <TextField
                      label="Last Name" variant="outlined" size="small" style={{ flex: 1 }}
                      value={newContact.lastName} onChange={(e) => setNewContact({ ...newContact, lastName: e.target.value })}
                    />
                  </Box>
                  <TextField
                    label="Company Name" variant="outlined" size="small" fullWidth
                    value={newContact.companyName} onChange={(e) => setNewContact({ ...newContact, companyName: e.target.value })}
                    style={{ marginBottom: 8 }}
                  />
                  <Box display="flex" style={{ gap: 8, marginBottom: 8 }}>
                    <TextField
                      label="Phone" variant="outlined" size="small" style={{ flex: 1 }}
                      value={newContact.phone} onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                    />
                    <TextField
                      label="Email" variant="outlined" size="small" style={{ flex: 1 }}
                      value={newContact.email} onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                    />
                  </Box>
                  <Button
                    size="small" variant="contained"
                    disabled={!newContact.firstName.trim() || (!newContact.phone.trim() && !newContact.email.trim())}
                    onClick={() => { setSelectedContact(null); setOneTimeStep('details') }}
                    style={{ backgroundColor: tokens.accent.brown, color: '#fff' }}
                  >
                    Next: Job Details
                  </Button>
                </Box>
              )}
              <Box mt={2}>
                <Button size="small" onClick={() => setOneTimeStep('site')} style={{ color: tokens.text.secondary }}>
                  &larr; Back to site selection
                </Button>
              </Box>
            </Box>
          )}

          {/* Step 3: Job Details */}
          {oneTimeStep === 'details' && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>Job Details</Typography>

              <Typography variant="caption" color="textSecondary" style={{ marginBottom: 4, display: 'block' }}>
                APPLICATION METHOD
              </Typography>
              <Box display="flex" style={{ gap: 8, marginBottom: 16 }}>
                {[
                  { value: 'broadcast', label: 'Broadcast', desc: 'Full coverage of all areas' },
                  { value: 'spot', label: 'Spot Spray', desc: 'Targeted treatment only' }
                ].map(opt => (
                  <Box
                    key={opt.value}
                    onClick={() => setJobDetails({ ...jobDetails, sprayMethod: opt.value })}
                    style={{
                      flex: 1, padding: '12px 16px', borderRadius: 6, cursor: 'pointer',
                      border: `2px solid ${jobDetails.sprayMethod === opt.value ? tokens.accent.brown : '#e0e0e0'}`,
                      backgroundColor: jobDetails.sprayMethod === opt.value ? tokens.accent.brownLight : '#fff'
                    }}
                  >
                    <Typography variant="body2" style={{ fontWeight: 600, fontSize: 13 }}>{opt.label}</Typography>
                    <Typography variant="caption" color="textSecondary">{opt.desc}</Typography>
                  </Box>
                ))}
              </Box>

              <Typography variant="caption" color="textSecondary" style={{ marginBottom: 4, display: 'block' }}>
                PRIORITY
              </Typography>
              <Box display="flex" style={{ gap: 8, marginBottom: 16 }}>
                {[
                  { value: 'normal', label: 'Normal', color: '#1565c0' },
                  { value: 'high', label: 'High', color: '#ef6c00' },
                  { value: 'rush', label: 'Rush', color: '#b71c1c' }
                ].map(opt => (
                  <Box
                    key={opt.value}
                    onClick={() => setJobDetails({ ...jobDetails, priority: opt.value })}
                    style={{
                      flex: 1, padding: '10px 12px', borderRadius: 6, cursor: 'pointer', textAlign: 'center',
                      border: `2px solid ${jobDetails.priority === opt.value ? opt.color : '#e0e0e0'}`,
                      backgroundColor: jobDetails.priority === opt.value ? `${opt.color}11` : '#fff'
                    }}
                  >
                    <Typography variant="body2" style={{
                      fontWeight: 700, fontSize: 13,
                      color: jobDetails.priority === opt.value ? opt.color : tokens.text.secondary
                    }}>
                      {opt.label}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Typography variant="caption" color="textSecondary" style={{ marginBottom: 4, display: 'block' }}>
                BLUE DYE
              </Typography>
              <Box display="flex" style={{ gap: 8, marginBottom: 16 }}>
                {[
                  { value: 'yes', label: 'Yes \u2014 Add blue dye' },
                  { value: 'no', label: 'No dye needed' }
                ].map(opt => (
                  <Box
                    key={opt.value}
                    onClick={() => setJobDetails({ ...jobDetails, blueDye: opt.value })}
                    style={{
                      flex: 1, padding: '10px 12px', borderRadius: 6, cursor: 'pointer', textAlign: 'center',
                      border: `2px solid ${jobDetails.blueDye === opt.value ? (opt.value === 'yes' ? '#1976d2' : '#78909c') : '#e0e0e0'}`,
                      backgroundColor: jobDetails.blueDye === opt.value ? (opt.value === 'yes' ? '#e3f2fd' : '#eceff1') : '#fff'
                    }}
                  >
                    <Typography variant="body2" style={{ fontWeight: 600, fontSize: 13 }}>{opt.label}</Typography>
                  </Box>
                ))}
              </Box>

              <Typography variant="caption" color="textSecondary" style={{ marginBottom: 4, display: 'block' }}>
                NOTES / SPECIAL INSTRUCTIONS
              </Typography>
              <TextField
                variant="outlined" size="small" fullWidth multiline rows={3}
                placeholder="Access instructions, hazards, special requirements..."
                value={jobDetails.notes}
                onChange={(e) => setJobDetails({ ...jobDetails, notes: e.target.value })}
                style={{ marginBottom: 12 }}
              />

              <Box display="flex" justifyContent="space-between">
                <Button size="small" onClick={() => setOneTimeStep('contact')} style={{ color: tokens.text.secondary }}>
                  &larr; Back
                </Button>
                <Button
                  size="small" variant="contained"
                  onClick={() => setOneTimeStep('review')}
                  style={{ backgroundColor: tokens.accent.brown, color: '#fff' }}
                >
                  Next: Review
                </Button>
              </Box>
            </Box>
          )}

          {/* Step 4: Review */}
          {oneTimeStep === 'review' && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>Review One-Time Job</Typography>
              <Box p={2} mb={2} style={{ border: '1px solid #e0e0e0', borderRadius: 4, backgroundColor: '#fafafa' }}>
                <Typography variant="caption" color="textSecondary">JOBSITE</Typography>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  {selectedSite ? selectedSite.siteName : newSite.name}
                  {!selectedSite && <span style={{ color: tokens.accent.brown, fontSize: 10, marginLeft: 6 }}>NEW</span>}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {selectedSite ? selectedSite.address : newSite.address}
                </Typography>
              </Box>
              <Box p={2} mb={2} style={{ border: '1px solid #e0e0e0', borderRadius: 4, backgroundColor: '#fafafa' }}>
                <Typography variant="caption" color="textSecondary">CONTACT</Typography>
                {selectedContact ? (
                  <>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                      {selectedContact.firstName || ''} {selectedContact.lastName || ''}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {[selectedContact.phoneNumber || selectedContact.phone, selectedContact.email].filter(Boolean).join(' \u00B7 ')}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                      {newContact.firstName} {newContact.lastName}
                      <span style={{ color: tokens.accent.brown, fontSize: 10, marginLeft: 6 }}>NEW</span>
                    </Typography>
                    {newContact.companyName && (
                      <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>{newContact.companyName}</Typography>
                    )}
                    <Typography variant="caption" color="textSecondary">
                      {[newContact.phone, newContact.email].filter(Boolean).join(' \u00B7 ')}
                    </Typography>
                  </>
                )}
              </Box>
              <Box p={2} mb={2} style={{ border: '1px solid #e0e0e0', borderRadius: 4, backgroundColor: '#fafafa' }}>
                <Typography variant="caption" color="textSecondary">JOB DETAILS</Typography>
                <Box display="flex" style={{ gap: 16, marginTop: 4 }}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Method</Typography>
                    <Typography variant="body2" style={{ fontWeight: 600, textTransform: 'capitalize' }}>
                      {jobDetails.sprayMethod === 'spot' ? 'Spot Spray' : 'Broadcast'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Priority</Typography>
                    <Typography variant="body2" style={{
                      fontWeight: 600, textTransform: 'capitalize',
                      color: jobDetails.priority === 'rush' ? '#b71c1c' : jobDetails.priority === 'high' ? '#ef6c00' : tokens.text.primary
                    }}>
                      {jobDetails.priority}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Blue Dye</Typography>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                      {jobDetails.blueDye === 'yes' ? 'Yes' : 'No'}
                    </Typography>
                  </Box>
                </Box>
                {jobDetails.notes && (
                  <Box mt={1}>
                    <Typography variant="caption" color="textSecondary">Notes</Typography>
                    <Typography variant="body2" style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>
                      {jobDetails.notes}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Box mt={1}>
                <Button size="small" onClick={() => setOneTimeStep('details')} style={{ color: tokens.text.secondary }}>
                  &larr; Back
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOneTimeOpen(false)}>Cancel</Button>
          {oneTimeStep === 'review' && (
            <Button
              variant="contained"
              disabled={!canSubmitOneTime() || submittingOneTime}
              onClick={submitOneTimeJob}
              style={{ backgroundColor: tokens.accent.brown, color: '#fff' }}
              startIcon={submittingOneTime ? <CircularProgress size={14} color="inherit" /> : null}
            >
              {submittingOneTime ? 'Creating...' : (canSubmitExisting() ? 'Create One-Time Job' : 'Create Job (needs setup)')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user
})

export default connect(mapStateToProps)(HerbicideQueueV4Dashboard)
